/**
 * 生成参数设置页（`settingPage` + `FJGeneratingSettingsPanel`）：对外 action 入口。
 */
import type { AppThunk } from '../appThunk';
import {
    DEFAULT_ORIGINAL_LANGUAGE_KEY,
    getDefaultTextLanguageKeyForLocale,
} from '@src/config/FJVideoSettingDataConfig';
import FJLocalStore from '@src/store/FJLocalStore';
import FJLongToShortStore from '@src/store/FJLongToShortStore';
import FJUserBehaviorStore from '@src/store/FJUserBehaviorStore';
import { beginGeneratingProgress, setGeneratingOverallProgress } from '../slice/FJGeneratingPageSlice';
import {
    resetSettingPage,
    setAspectRatioKey,
    setCaptureKeyMoments,
    setClipLengthKey,
    setGenreKey,
    setOriginalLanguageKey,
    setSubtitleStyleId,
    setTextLanguageKey,
} from '../slice/FJSettingPageSlice';
import { FJ_COMMON_VIEW, setActiveView } from '../slice/FJCommonSlice';

import { FJStartLandingAction } from './FJStartLandingAction';
import type { ProjectItem } from '../../types/FJProject';
import { GENERATE_STATE } from '@src/constants/FJEnumConstant';
import { FJFailedPageAction } from './FJFailedPageAction';
import { FJCommonAction } from './FJCommonAction';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';
import {
    buildLongToShortsGenerateFailEaFromTaskCallback,
    clearLtsGenerateContext,
    eaLogLongToShortsGenerateFail,
    flattenGenerateEaPayload,
    getLtsUploadSource,
    inferVideoFormat,
    LTS_GENERATE_FAIL_REASON_CREATE_TASK_NO_EVENT_ID,
    stashLtsGenerateContext,
    type LtsGenerateContext,
} from '@src/util/FJLongToShortEa';
export type { SettingPageState } from '../slice/FJSettingPageSlice';

export const FJSettingPageAction = {
    /** 进入「生成参数」面板 */
    openGenSettingsAction: (): AppThunk => async (dispatch, getState) => {
        void getState();
        dispatch(resetSettingPage());
        dispatch(setOriginalLanguageKey(DEFAULT_ORIGINAL_LANGUAGE_KEY));
        const savedTextLanguageKey = await FJUserBehaviorStore.getTextLanguageKey();
        dispatch(
            setTextLanguageKey(savedTextLanguageKey ?? getDefaultTextLanguageKeyForLocale(FJLocalStore.currentTag())),
        );
        dispatch(setActiveView(FJ_COMMON_VIEW.SETTING));
    },

    /** 在设置页点击生成：进入生成进度并启动上传与任务创建 */
    generateAction: (): AppThunk => async (dispatch, getState) => {
        if (!FJLongToShortStore.getFileData()) {
            return;
        }
        const { textLanguageKey, genreKey, clipLengthKey, aspectRatioKey, subtitleStyleId, captureKeyMoments } =
            getState().settingPage;
        const { totalCredits, freeTimes } = getState().common;
        const { video } = getState().generatingPage;
        const duration = FJLongToShortStore.getFileDuration();
        const canGenerate = FJLongToShortStore.beforeCheck(totalCredits, freeTimes, duration);
        if (!canGenerate) {
            return;
        }
        const fd = FJLongToShortStore.getFileData() as { file?: File; fileDownLoadUrl?: string } | null | undefined;
        const fileDur = Number(duration ?? 0);
        const useCreditMinutes = FJLongToShortStore.getFileUseCreditAmount(fileDur);
        const whetherTrial = totalCredits < useCreditMinutes && freeTimes > 0;
        const credits = whetherTrial ? 0 : useCreditMinutes;
        const momentsTrim = (captureKeyMoments ?? '').trim();
        const clipDurSetting = (clipLengthKey ?? '').trim() || 'auto';
        const genCtx: LtsGenerateContext = {
            source: getLtsUploadSource(),
            URL: (video.sourceUrl ?? '').trim() || '/',
            duration: Number.isFinite(fileDur) ? fileDur : 0,
            format: inferVideoFormat(fd?.file || (fd?.fileDownLoadUrl as string)),
            text_language: textLanguageKey,
            clip_type: genreKey,
            clip_duration: clipDurSetting,
            ratio: aspectRatioKey,
            subtitle: subtitleStyleId,
            moments: momentsTrim.length ? momentsTrim : '/',
            whether_trial: whetherTrial,
            credits,
        };
        stashLtsGenerateContext(genCtx);
        FJRequirementsUtil.eaLog(
            'long_to_shorts_generate_click',
            flattenGenerateEaPayload(genCtx) as Record<string, unknown>,
        );

        dispatch(setActiveView(FJ_COMMON_VIEW.GENERATING));
        dispatch(beginGeneratingProgress());
        FJLongToShortStore.setCurFailedTaskId(null);
        let eventId = '';
        eventId = await FJLongToShortStore.start({
            textLanguageKey,
            genreKey,
            clipLengthKey,
            aspectRatioKey,
            subtitleStyleId,
            captureKeyMoments,
            videoMeta: video,
            progressCallback: ({ id, progress }: { id: string; progress: number }) => {
                // 上传阶段无id
                const p = Math.min(100, progress * 100);
                dispatch(setGeneratingOverallProgress(p));
                dispatch(FJStartLandingAction.updataProjectProgressAction(id, p));
            },
            callback: (item: ProjectItem) => {
                if (item?.status === GENERATE_STATE.PENDING) {
                    dispatch(FJStartLandingAction.runPendingAction(item as ProjectItem));
                } else {
                    eaLogLongToShortsGenerateFail(buildLongToShortsGenerateFailEaFromTaskCallback(item, eventId));
                    clearLtsGenerateContext();
                    dispatch(
                        FJStartLandingAction.syncProjectGenerateFailedAction({
                            eventId: eventId || (item as ProjectItem | undefined)?.eventId || '',
                            errorCode: (item as ProjectItem | undefined)?.errorCode,
                            taskItem: item as ProjectItem | undefined,
                        }),
                    );
                    dispatch(FJFailedPageAction.showFailedPageAction());
                    FJLongToShortStore.setCurFailedTaskId(
                        eventId || (item as ProjectItem | undefined)?.eventId,
                        (item as ProjectItem | undefined)?.errorCode,
                    );
                }
                dispatch(FJCommonAction.fetchTaskListAction());
                dispatch(FJCommonAction.updateTotalCreditsAction());
            },
        });
        if (!eventId) {
            eaLogLongToShortsGenerateFail({
                project_id: '',
                fail_reason: LTS_GENERATE_FAIL_REASON_CREATE_TASK_NO_EVENT_ID,
                error_code: 'no_event_id',
            });
            clearLtsGenerateContext();
        }
        dispatch(FJCommonAction.fetchTaskListAction());
    },

    setOriginalLanguageKeyAction:
        (key: string): AppThunk =>
        (dispatch, getState) => {
            void getState();
            dispatch(setOriginalLanguageKey(key));
        },

    setTextLanguageKeyAction:
        (key: string): AppThunk =>
        (dispatch, getState) => {
            void getState();
            dispatch(setTextLanguageKey(key));
            void FJUserBehaviorStore.setTextLanguageKey(key);
        },

    setGenreKeyAction:
        (key: string): AppThunk =>
        (dispatch, getState) => {
            void getState();
            dispatch(setGenreKey(key));
        },

    setClipLengthKeyAction:
        (key: string): AppThunk =>
        (dispatch, getState) => {
            void getState();
            dispatch(setClipLengthKey(key));
        },

    setAspectRatioKeyAction:
        (key: string): AppThunk =>
        (dispatch, getState) => {
            void getState();
            dispatch(setAspectRatioKey(key));
        },

    setSubtitleStyleIdAction:
        (id: string): AppThunk =>
        (dispatch, getState) => {
            void getState();
            dispatch(setSubtitleStyleId(id));
        },

    setCaptureKeyMomentsAction:
        (text: string): AppThunk =>
        (dispatch, getState) => {
            void getState();
            dispatch(setCaptureKeyMoments(text));
        },
} as const;
